import { GreenhouseCustomFieldId, GreenhouseJob, SAVED_JOBS_COOKIE, JOB_ID_SEPARATOR } from './greenhouse';
import { slugify } from '@/utils/formatters';

export const getJobsByGreenhouseField = (jobs: GreenhouseJob[], fieldId: GreenhouseCustomFieldId) => {
  return jobs.filter(job => job.metadata?.find(metadata => metadata.id === fieldId)?.value === true);
};

export const getJobTeam = (job: GreenhouseJob): string | null => {
  const value = job.metadata?.find(metadata => metadata.id === GreenhouseCustomFieldId.Team)?.value;
  return typeof value === 'string' && value.length > 0 ? value : null;
};

export const getJobUrl = (job: GreenhouseJob): string => {
  return `/jobs/${job.id}/${slugify(job.title)}`;
};

export const getJobLocations = (jobs: GreenhouseJob[]): string[] =>
  [
    ...new Set(
      jobs.flatMap(job =>
        job.location.name
          .split(';')
          .map(loc => loc.trim())
          .filter(Boolean),
      ),
    ),
  ].sort((a, b) => a.localeCompare(b));

export const getJobTeams = (jobs: GreenhouseJob[]): string[] =>
  [
    ...new Set(
      jobs.flatMap(job => {
        const team = getJobTeam(job);
        return team !== null ? [team] : [];
      }),
    ),
  ].sort((a, b) => a.localeCompare(b));

let lastCookieString = '';
let cachedIds: number[] = [];

function getSavedJobsCookieValue(): string {
  if (typeof document === 'undefined') return '';
  return (
    document.cookie
      .split('; ')
      .find(row => row.startsWith(`${SAVED_JOBS_COOKIE}=`))
      ?.split('=')[1] || ''
  );
}

function parseSavedJobIds(cookieValue: string): number[] {
  return cookieValue ? cookieValue.split(JOB_ID_SEPARATOR).filter(Boolean).map(Number) : [];
}

export function setSavedJobsCookie(ids: number[]): void {
  if (typeof document === 'undefined') return;
  const value = ids.length ? ids.join(JOB_ID_SEPARATOR) : '';
  document.cookie = `${SAVED_JOBS_COOKIE}=${value}; path=/; max-age=31536000; SameSite=Lax`;
}

export const getSavedJobIds = (): number[] => {
  if (typeof document === 'undefined') return [];

  const currentCookie = getSavedJobsCookieValue();
  if (currentCookie !== lastCookieString) {
    lastCookieString = currentCookie;
    cachedIds = parseSavedJobIds(currentCookie);
  }
  return cachedIds;
};

export const pruneSavedJobIds = (existingJobIds: number[]): boolean => {
  if (typeof document === 'undefined') return false;

  const cookieValue = getSavedJobsCookieValue();
  const savedIds = parseSavedJobIds(cookieValue);
  const prunedIds = savedIds.filter(id => existingJobIds.includes(id));
  if (prunedIds.length === savedIds.length) return false;

  setSavedJobsCookie(prunedIds);
  lastCookieString = '';
  cachedIds = [];
  return true;
};
